import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export function Privacy() {
  return (
    <div className="min-h-screen bg-[#0a0f0a] text-gray-300">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-8">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Last updated: January 25, 2026</p>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">1. Introduction</h2>
            <p>
              SentimentEdge ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">2. Information We Collect</h2>

            <h3 className="text-xl font-medium text-white mt-4 mb-2">Personal Information</h3>
            <p>When you create an account, we may collect:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Email address</li>
              <li>Name (if provided)</li>
              <li>Password (stored securely hashed)</li>
              <li>Payment information (processed by Stripe, not stored by us)</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-4 mb-2">Usage Information</h3>
            <p>We automatically collect:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Log data (IP address, browser type, pages visited)</li>
              <li>Device information</li>
              <li>Usage patterns and preferences</li>
              <li>Stocks you track and analyze</li>
            </ul>

            <h3 className="text-xl font-medium text-white mt-4 mb-2">Third-Party Authentication</h3>
            <p>
              If you sign in with Google or X (Twitter), we receive your public profile information
              as authorized by those platforms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">3. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Provide and maintain the Service</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative updates and security alerts</li>
              <li>Respond to customer service requests</li>
              <li>Improve and personalize the Service</li>
              <li>Analyze usage patterns to enhance features</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">4. Data Sharing</h2>
            <p>We may share your information with:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Service Providers:</strong> Third parties that help us operate the Service (e.g., Stripe for payments, cloud hosting providers)</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
            <p className="mt-4 font-semibold text-emerald-400">
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">5. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your data, including:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Encryption in transit (HTTPS/TLS)</li>
              <li>Encrypted database storage</li>
              <li>Secure password hashing (bcrypt)</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
            <p className="mt-4">
              However, no method of transmission over the Internet is 100% secure. We cannot guarantee
              absolute security of your data.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">6. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to
              provide services. You may request deletion of your account and associated data at any time.
              Some information may be retained for legal or legitimate business purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">7. Your Rights</h2>
            <p>Depending on your location, you may have the right to:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict certain processing</li>
              <li>Data portability (receive your data in a structured format)</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@stocksentiment.io" className="text-emerald-400 hover:text-emerald-300">
                privacy@stocksentiment.io
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">8. Cookies and Tracking</h2>
            <p>
              We use essential cookies to maintain your session and preferences. We may also use
              analytics cookies to understand how you use the Service. You can control cookie
              preferences through your browser settings.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">9. Third-Party Services</h2>
            <p>Our Service integrates with third-party services including:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><strong>Stripe:</strong> Payment processing - see <a href="https://stripe.com/privacy" className="text-emerald-400 hover:text-emerald-300" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a></li>
              <li><strong>Google:</strong> OAuth authentication - see <a href="https://policies.google.com/privacy" className="text-emerald-400 hover:text-emerald-300" target="_blank" rel="noopener noreferrer">Google's Privacy Policy</a></li>
              <li><strong>X (Twitter):</strong> OAuth and sentiment data - see <a href="https://twitter.com/privacy" className="text-emerald-400 hover:text-emerald-300" target="_blank" rel="noopener noreferrer">X's Privacy Policy</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">10. Children's Privacy</h2>
            <p>
              The Service is not intended for users under 18 years of age. We do not knowingly collect
              personal information from children. If you believe we have collected information from a
              child, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">11. International Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your own.
              We ensure appropriate safeguards are in place for such transfers in compliance with
              applicable data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes
              by posting the updated policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">13. Contact Us</h2>
            <p>
              For questions or concerns about this Privacy Policy, please contact us at:
            </p>
            <p className="mt-2">
              <a href="mailto:privacy@stocksentiment.io" className="text-emerald-400 hover:text-emerald-300">
                privacy@stocksentiment.io
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          <Link to="/" className="text-emerald-400 hover:text-emerald-300">
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
